import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { pickDetail } from '../lib/data'
import TierBadge, { TIER_STYLE } from '../components/TierBadge'

// Weekly executive report — designed for Karen and the ModWash leadership
// team. Renders live data from the platform with a layout that:
//   - Reads cleanly on screen
//   - Prints to PDF cleanly (Cmd+P → Save as PDF)
//   - Has a "Copy as plain text" button so the same content can be
//     pasted straight into an email body
//
// Visible to admin / executive / regional_ops. The auto-email layer
// (Vercel cron + Resend) is a follow-up — for now this is reviewed
// manually before each Monday send.
//
// Tone is deliberately honest. Zero counts (e.g., "0 sites verified this
// week") are shown as zero rather than hidden. The whole point of the
// engagement metric is to watch those numbers grow week over week.

const TIERS = ['critical','high','medium','low']

export default function WeeklyReport() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true); setError(null)
    // Two windows: this week (last 7 days) and previous week (8–14 days ago).
    // Pulling 14 days lets us show WoW deltas without a second query round trip.
    // The header still labels the actual rolling-7-day range; the previous
    // week is reconstructed in computeStats() from the same dataset.
    const now = Date.now()
    const weekAgo      = new Date(now -  7 * 24 * 60 * 60 * 1000)
    const twoWeeksAgo  = new Date(now - 14 * 24 * 60 * 60 * 1000)
    const weekAgoIso     = weekAgo.toISOString()
    const twoWeeksAgoIso = twoWeeksAgo.toISOString()

    const [sitesRes, ticketsRes, auditsRes] = await Promise.all([
      supabase.from('sites')
        .select('id, name, city, state, active, site_details(*)')
        .eq('active', true),
      supabase.from('tickets')
        .select('id, ticket_number, status, tier, type, source, created_at, resolved_at, sites(name, site_details(site_code))'),
      supabase.from('audits')
        .select('id, status, created_at, submitted_at, sites(name, site_details(site_code))')
        .gte('created_at', twoWeeksAgoIso),
    ])

    if (sitesRes.error || ticketsRes.error || auditsRes.error) {
      setError(sitesRes.error?.message || ticketsRes.error?.message || auditsRes.error?.message)
      setLoading(false)
      return
    }

    setData({
      sites:    sitesRes.data    || [],
      tickets:  ticketsRes.data  || [],
      audits:   auditsRes.data   || [],
      weekAgoIso,
      twoWeeksAgoIso,
      generatedAt: new Date(),
    })
    setLoading(false)
  }

  const stats = useMemo(() => data ? computeStats(data) : null, [data])

  function handleCopy() {
    if (!stats) return
    const text = renderPlainText(stats, data)
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (loading) return <div className="spinner" />
  if (error)   return <div className="empty-state"><h3>Couldn't load report data</h3><p>{error}</p></div>

  return (
    <div className="page-container weekly-report">
      {/* Action bar (hidden in print) */}
      <div className="no-print" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12, gap: 8, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Live data · refresh anytime · print or copy to email
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" onClick={fetchAll}>Refresh</button>
          <button className="btn btn-sm" onClick={handleCopy}>
            {copied ? '✓ Copied' : 'Copy as text'}
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => window.print()}>
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* === REPORT BEGINS === */}
      <ReportHeader stats={stats} />
      <HeadlineMetrics stats={stats} />
      <ActivityThisWeek stats={stats} />
      <VerificationVelocity stats={stats} />
      <TierDistribution stats={stats} navigate={navigate} />
      <TopPriorityUnverified stats={stats} navigate={navigate} />
      <ManualEntryWatchList stats={stats} navigate={navigate} />
      <ReportFooter stats={stats} />
    </div>
  )
}

// ============== Sections ==============

function ReportHeader({ stats }) {
  const start = stats.weekStart.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
  const end   = stats.weekEnd.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
  return (
    <div style={{ marginBottom: 18, paddingBottom: 12, borderBottom: '2px solid var(--text)' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
        ModWash · Weekly Operations Report
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}>
        Week of {start} — {end}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
        Generated {stats.generatedAt.toLocaleString()} from carwashaudit.com
      </div>
    </div>
  )
}

function HeadlineMetrics({ stats }) {
  return (
    <Section title="Headline">
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10,
      }}>
        <Metric
          label="Sites surveyed"
          value={`${stats.surveyedSites}`}
          sub={`of ${stats.totalSites} active`}
        />
        <Metric
          label="Sites verified"
          value={`${stats.verifiedSites}`}
          sub={
            stats.verifiedThisWeek === 0 && stats.verifiedPrevWeek === 0
              ? 'none yet — pilot starts this week'
              : <>+{stats.verifiedThisWeek} this week · <Delta current={stats.verifiedThisWeek} previous={stats.verifiedPrevWeek} goodWhen="higher" /></>
          }
          tone={stats.verifiedSites > 0 ? 'good' : 'neutral'}
        />
        <Metric
          label="Hardware investment"
          value={`$${stats.totalHardwareCost.toLocaleString()}`}
          sub="estimated total"
        />
        <Metric
          label="Fleet revenue"
          value={`$${(stats.totalWeeklyRevenue * 52 / 1_000_000).toFixed(1)}M`}
          sub="annualized"
        />
      </div>
    </Section>
  )
}

function ActivityThisWeek({ stats }) {
  // Build the source breakdown sub-line so it always sums to the total.
  // Hide buckets that are zero so the line stays readable.
  const breakdown = []
  if (stats.ticketsAuditDrivenThisWeek)  breakdown.push(`${stats.ticketsAuditDrivenThisWeek} from audits`)
  if (stats.ticketsSurveyDrivenThisWeek) breakdown.push(`${stats.ticketsSurveyDrivenThisWeek} from survey findings`)
  if (stats.ticketsManualThisWeek)       breakdown.push(`${stats.ticketsManualThisWeek} created manually`)
  if (stats.ticketsOtherThisWeek)        breakdown.push(`${stats.ticketsOtherThisWeek} other`)
  const breakdownText = breakdown.length ? breakdown.join(' · ') : '—'

  return (
    <Section title="Activity this week">
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10,
      }}>
        <Metric
          label="Audits started"
          value={stats.auditsThisWeek}
          sub={<Delta current={stats.auditsThisWeek} previous={stats.auditsPrevWeek} goodWhen="higher" />}
        />
        <Metric
          label="Tickets opened"
          value={stats.ticketsOpenedThisWeek}
          sub={<>{breakdownText} · <Delta current={stats.ticketsOpenedThisWeek} previous={stats.ticketsOpenedPrevWeek} goodWhen="lower" /></>}
        />
        <Metric
          label="Tickets resolved"
          value={stats.ticketsResolvedThisWeek}
          sub={<Delta current={stats.ticketsResolvedThisWeek} previous={stats.ticketsResolvedPrevWeek} goodWhen="higher" />}
          tone={stats.ticketsResolvedThisWeek > 0 ? 'good' : 'neutral'}
        />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
        Currently {stats.openTickets} open tickets across the fleet
        ({stats.openCriticalTickets} Critical, {stats.openHighTickets} High).
      </div>
    </Section>
  )
}

function VerificationVelocity({ stats }) {
  const pct = stats.surveyedSites > 0
    ? Math.round((stats.verifiedSites / stats.surveyedSites) * 100)
    : 0
  return (
    <Section title="Verification progress">
      <div style={{ fontSize: 13, marginBottom: 8 }}>
        <strong>{stats.verifiedSites} of {stats.surveyedSites}</strong> surveyed sites verified ({pct}%).
        {stats.verifiedSites === 0 && (
          <span style={{ color: 'var(--text-muted)' }}> Pilot verification of the four Pittsburgh sites begins this week.</span>
        )}
      </div>
      <div style={{ height: 10, background: '#F1EFE8', borderRadius: 5, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: pct > 50 ? '#3B6D11' : pct > 20 ? '#854F0B' : '#A32D2D',
          transition: 'width 0.3s ease',
        }} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
        Verification means a qualified reviewer (NXT remote tech or MCWW field service)
        has confirmed or amended the original survey diagnosis at the site. This is the
        primary metric to watch grow week over week.
      </div>
    </Section>
  )
}

function TierDistribution({ stats, navigate }) {
  return (
    <Section title="Priority tier distribution">
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10,
      }}>
        {TIERS.map(tier => {
          const count = stats.tierCounts[tier] || 0
          const t = TIER_STYLE[tier]
          const sharePct = stats.surveyedSites > 0 ? (count / stats.surveyedSites) * 100 : 0
          return (
            <div key={tier} className="no-print-hover"
              style={{
                background: t.bg, color: t.color, padding: 12, borderRadius: 10,
                cursor: 'pointer',
              }}
              onClick={() => navigate(`/sites?tier=${tier}`)}>
              <div style={{ fontSize: 11, opacity: 0.8 }}>{t.dot} {t.label}</div>
              <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4, lineHeight: 1 }}>{count}</div>
              <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                {sharePct.toFixed(0)}% of surveyed
              </div>
            </div>
          )
        })}
      </div>
    </Section>
  )
}

function TopPriorityUnverified({ stats, navigate }) {
  if (stats.topUnverified.length === 0) return null
  return (
    <Section title="Top priority sites (still unverified)">
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
        The five highest-scoring sites that have not yet been reviewed by a qualified verifier.
      </div>
      {stats.topUnverified.map(d => (
        <SiteRow key={d.site_id} d={d} onClick={() => navigate(`/sites/${d.site_id}`)} />
      ))}
    </Section>
  )
}

function ManualEntryWatchList({ stats, navigate }) {
  if (stats.highManualSites.length === 0) return null
  return (
    <Section title="Manual entry watch list">
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
        {stats.highManualSites.length} site{stats.highManualSites.length === 1 ? '' : 's'} entering &gt;10% of plates manually at the kiosk.
        Each manual entry is friction at the kiosk and a missed LPR opportunity.
      </div>
      {stats.highManualSites.slice(0, 8).map(d => (
        <SiteRow key={d.site_id} d={d} onClick={() => navigate(`/sites/${d.site_id}`)}
          showManualPct />
      ))}
      {stats.highManualSites.length > 8 && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, fontStyle: 'italic' }}>
          + {stats.highManualSites.length - 8} more
        </div>
      )}
    </Section>
  )
}

function ReportFooter({ stats }) {
  return (
    <div style={{
      marginTop: 24, paddingTop: 12, borderTop: '1px solid var(--border)',
      fontSize: 11, color: 'var(--text-muted)', textAlign: 'center',
    }}>
      <div>Live dashboards: <strong>https://www.carwashaudit.com/fleet</strong></div>
      <div style={{ marginTop: 4 }}>
        For questions, reply to this email or contact Ian Beason · ibeason@mcww.com
      </div>
    </div>
  )
}

// ============== Building blocks ==============

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 12, breakInside: 'avoid' }}>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10, fontWeight: 500 }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function Metric({ label, value, sub, tone }) {
  const colorMap = {
    good:    '#3B6D11',
    bad:     '#A32D2D',
    neutral: 'var(--text)',
  }
  return (
    <div style={{
      padding: 10, borderRadius: 8,
      background: tone === 'good' ? '#EAF3DE' : tone === 'bad' ? '#FCEBEB' : '#fff',
      border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{
        fontSize: 22, fontWeight: 600, marginTop: 4, lineHeight: 1,
        color: colorMap[tone] || colorMap.neutral,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
      )}
    </div>
  )
}

// Inline week-over-week delta. `goodWhen='higher'` colors a positive change
// green (e.g. tickets resolved up = good). `goodWhen='lower'` colors
// decreases green (e.g. open queue size shrinking = good). Returns null
// when both windows are zero so we don't add visual noise to brand-new
// metrics that haven't accumulated data yet.
function Delta({ current, previous, goodWhen = 'higher' }) {
  if ((current ?? 0) === 0 && (previous ?? 0) === 0) return null
  const diff = (current ?? 0) - (previous ?? 0)
  if (diff === 0) {
    return <span style={{ color: 'var(--text-muted)' }}>flat vs last wk</span>
  }
  const isGood = (goodWhen === 'higher' && diff > 0) || (goodWhen === 'lower' && diff < 0)
  const color = isGood ? '#3B6D11' : '#A32D2D'
  const sign  = diff > 0 ? '+' : ''
  return <span style={{ color, fontWeight: 500 }}>{sign}{diff} vs last wk</span>
}

function SiteRow({ d, onClick, showManualPct }) {
  const cameraStand = d.camera_mount === 'stand'
  return (
    <div onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 0', cursor: 'pointer',
        borderTop: '1px solid var(--border)',
      }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
            {d.site_code}
          </span>
          <span style={{ fontWeight: 500, fontSize: 14 }}>{d.site_name}</span>
          <TierBadge tier={d.priority_tier} rank={d.priority_rank} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {[d.city, d.state].filter(Boolean).join(', ')}
          {d.district && ` · ${d.district}`}
          {cameraStand && <span style={{ color: '#A32D2D' }}> · ⚠️ stand cams</span>}
        </div>
      </div>
      {showManualPct && d.manual_lp_pct != null && (
        <span style={{
          fontSize: 13, fontWeight: 500, color: '#A32D2D',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {(Number(d.manual_lp_pct) * 100).toFixed(1)}%
        </span>
      )}
    </div>
  )
}

// ============== Stats computation ==============

function computeStats(data) {
  const weekStart = new Date(data.weekAgoIso)
  const weekEnd   = new Date(data.generatedAt)

  // Build site rollup with normalized det
  const sitesNorm = data.sites.map(s => {
    const det = pickDetail(s.site_details)
    return det ? {
      site_id: s.id,
      site_name: s.name,
      city: s.city,
      state: s.state,
      site_code:      det.site_code,
      district:       det.district,
      priority_score: det.priority_score == null ? null : Number(det.priority_score),
      priority_tier:  det.priority_tier,
      priority_rank:  det.priority_rank ?? det.rank,
      camera_mount:   det.camera_mount,
      manual_lp_pct:  det.manual_lp_pct == null ? null : Number(det.manual_lp_pct),
      survey_verified: !!det.survey_verified,
      survey_verified_at: det.survey_verified_at,
      camera_update_cost:    Number(det.camera_update_cost || 0),
      lpr_stand_update_cost: Number(det.lpr_stand_update_cost || 0),
      weekly_net_sales:      Number(det.weekly_net_sales || 0),
    } : null
  }).filter(Boolean)

  // Tier counts (only surveyed sites)
  const tierCounts = { critical: 0, high: 0, medium: 0, low: 0 }
  let surveyedSites = 0
  let verifiedSites = 0
  let verifiedThisWeek = 0
  let totalCameraCost = 0
  let totalLprStandCost = 0
  let totalWeeklyRevenue = 0

  for (const s of sitesNorm) {
    if (s.priority_tier && tierCounts[s.priority_tier] != null) {
      tierCounts[s.priority_tier]++
      surveyedSites++
    }
    if (s.survey_verified) {
      verifiedSites++
      if (s.survey_verified_at && s.survey_verified_at >= data.weekAgoIso) verifiedThisWeek++
    }
    totalCameraCost    += s.camera_update_cost || 0
    totalLprStandCost  += s.lpr_stand_update_cost || 0
    totalWeeklyRevenue += s.weekly_net_sales || 0
  }

  // Tickets — break out by source/status/window.
  // We track both this-week (last 7 days) and prev-week (8–14 days ago)
  // so the report can render WoW deltas. Source buckets are also broken
  // out exhaustively so the breakdown sums to the total — previously
  // 'manual' / 'system' / 'import' were silently dropped, making the
  // headline number not reconcile with its subtitle.
  const isResolved   = t => t.status === 'resolved'
  const isCancelled  = t => t.status === 'cancelled'
  const isOpenStatus = t => !isResolved(t) && !isCancelled(t)

  const inThisWeek = iso => iso && iso >= data.weekAgoIso
  const inPrevWeek = iso => iso && iso >= data.twoWeeksAgoIso && iso < data.weekAgoIso

  // This-week ticket buckets
  const ticketsThisWeek            = data.tickets.filter(t => inThisWeek(t.created_at))
  const ticketsOpenedThisWeek      = ticketsThisWeek.length
  const ticketsAuditDrivenThisWeek = ticketsThisWeek.filter(t => t.source === 'audit_finding').length
  const ticketsSurveyDrivenThisWeek= ticketsThisWeek.filter(t => t.source === 'survey').length
  const ticketsManualThisWeek      = ticketsThisWeek.filter(t => t.source === 'manual').length
  const ticketsOtherThisWeek       =
    ticketsOpenedThisWeek
    - ticketsAuditDrivenThisWeek
    - ticketsSurveyDrivenThisWeek
    - ticketsManualThisWeek
  const ticketsResolvedThisWeek    = data.tickets.filter(t => isResolved(t) && inThisWeek(t.resolved_at)).length

  // Prev-week ticket buckets (same shape, different window)
  const ticketsOpenedPrevWeek      = data.tickets.filter(t => inPrevWeek(t.created_at)).length
  const ticketsResolvedPrevWeek    = data.tickets.filter(t => isResolved(t) && inPrevWeek(t.resolved_at)).length

  const openTickets         = data.tickets.filter(isOpenStatus).length
  const openCriticalTickets = data.tickets.filter(t => isOpenStatus(t) && t.tier === 'Critical').length
  const openHighTickets     = data.tickets.filter(t => isOpenStatus(t) && t.tier === 'High').length

  // Audits by window
  const auditsThisWeek = data.audits.filter(a => inThisWeek(a.created_at)).length
  const auditsPrevWeek = data.audits.filter(a => inPrevWeek(a.created_at)).length

  // Verifications by window — re-derived from sitesNorm so we don't need to
  // run a third query. verifiedThisWeek already exists from the loop above;
  // verifiedPrevWeek is the same filter against the prior window.
  let verifiedPrevWeek = 0
  for (const s of sitesNorm) {
    if (s.survey_verified && inPrevWeek(s.survey_verified_at)) verifiedPrevWeek++
  }

  // Top 5 unverified by score
  const topUnverified = sitesNorm
    .filter(s => !s.survey_verified && s.priority_score != null)
    .sort((a, b) => b.priority_score - a.priority_score)
    .slice(0, 5)

  // Manual entry watch list
  const highManualSites = sitesNorm
    .filter(s => s.manual_lp_pct != null && s.manual_lp_pct > 0.10)
    .sort((a, b) => b.manual_lp_pct - a.manual_lp_pct)

  return {
    weekStart, weekEnd,
    weekAgoIso: data.weekAgoIso,
    generatedAt: data.generatedAt,
    totalSites: sitesNorm.length,
    surveyedSites,
    verifiedSites,
    verifiedThisWeek,
    tierCounts,
    totalCameraCost,
    totalLprStandCost,
    totalHardwareCost: totalCameraCost + totalLprStandCost,
    totalWeeklyRevenue,
    auditsThisWeek,
    auditsPrevWeek,
    ticketsOpenedThisWeek,
    ticketsOpenedPrevWeek,
    ticketsResolvedThisWeek,
    ticketsResolvedPrevWeek,
    ticketsAuditDrivenThisWeek,
    ticketsSurveyDrivenThisWeek,
    ticketsManualThisWeek,
    ticketsOtherThisWeek,
    verifiedPrevWeek,
    openTickets,
    openCriticalTickets,
    openHighTickets,
    topUnverified,
    highManualSites,
  }
}

// ============== Plain-text version (for email pasting) ==============

function renderPlainText(stats) {
  const start = stats.weekStart.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
  const end   = stats.weekEnd.toLocaleDateString(undefined,   { month: 'long', day: 'numeric', year: 'numeric' })
  const lines = []
  lines.push(`MODWASH WEEKLY OPERATIONS REPORT`)
  lines.push(`Week of ${start} — ${end}`)
  lines.push(`Generated ${stats.generatedAt.toLocaleString()}`)
  lines.push('')
  lines.push(`HEADLINE`)
  lines.push(`  Sites surveyed:       ${stats.surveyedSites} of ${stats.totalSites} active`)
  lines.push(`  Sites verified:       ${stats.verifiedSites}` + fmtDelta(stats.verifiedThisWeek, stats.verifiedPrevWeek, 'this week'))
  lines.push(`  Hardware investment:  $${stats.totalHardwareCost.toLocaleString()}`)
  lines.push(`  Fleet revenue:        $${(stats.totalWeeklyRevenue * 52 / 1_000_000).toFixed(1)}M annualized`)
  lines.push('')
  lines.push(`ACTIVITY THIS WEEK`)
  // Reconcile breakdown so it sums to total
  const tBreak = []
  if (stats.ticketsAuditDrivenThisWeek)  tBreak.push(`${stats.ticketsAuditDrivenThisWeek} from audits`)
  if (stats.ticketsSurveyDrivenThisWeek) tBreak.push(`${stats.ticketsSurveyDrivenThisWeek} from survey findings`)
  if (stats.ticketsManualThisWeek)       tBreak.push(`${stats.ticketsManualThisWeek} manual`)
  if (stats.ticketsOtherThisWeek)        tBreak.push(`${stats.ticketsOtherThisWeek} other`)
  lines.push(`  Audits started:       ${stats.auditsThisWeek}` + fmtDelta(stats.auditsThisWeek, stats.auditsPrevWeek))
  lines.push(`  Tickets opened:       ${stats.ticketsOpenedThisWeek}` + (tBreak.length ? ` (${tBreak.join(', ')})` : '') + fmtDelta(stats.ticketsOpenedThisWeek, stats.ticketsOpenedPrevWeek))
  lines.push(`  Tickets resolved:     ${stats.ticketsResolvedThisWeek}` + fmtDelta(stats.ticketsResolvedThisWeek, stats.ticketsResolvedPrevWeek))
  lines.push(`  Currently open:       ${stats.openTickets} (${stats.openCriticalTickets} Critical, ${stats.openHighTickets} High)`)
  lines.push('')
  lines.push(`TIER DISTRIBUTION`)
  for (const tier of TIERS) {
    const count = stats.tierCounts[tier] || 0
    const pct = stats.surveyedSites > 0 ? Math.round((count / stats.surveyedSites) * 100) : 0
    lines.push(`  ${(tier.charAt(0).toUpperCase() + tier.slice(1)).padEnd(10)} ${String(count).padStart(3)}  (${pct}%)`)
  }
  lines.push('')
  if (stats.topUnverified.length > 0) {
    lines.push(`TOP PRIORITY UNVERIFIED (top 5)`)
    for (const s of stats.topUnverified) {
      lines.push(`  ${s.site_code.padEnd(8)} ${s.site_name}  —  ${s.priority_tier} #${s.priority_rank || '?'} (score ${s.priority_score?.toFixed(1)})`)
    }
    lines.push('')
  }
  if (stats.highManualSites.length > 0) {
    lines.push(`MANUAL ENTRY WATCH LIST (>10%)`)
    for (const s of stats.highManualSites.slice(0, 10)) {
      const pct = (s.manual_lp_pct * 100).toFixed(1)
      lines.push(`  ${s.site_code.padEnd(8)} ${s.site_name.padEnd(40)} ${pct}%`)
    }
    if (stats.highManualSites.length > 10) lines.push(`  + ${stats.highManualSites.length - 10} more`)
    lines.push('')
  }
  lines.push(`Live dashboards: https://www.carwashaudit.com/fleet`)
  lines.push(`Questions: Ian Beason · ibeason@mcww.com`)
  return lines.join('\n')
}

// Plain-text delta suffix — e.g. "  (+3 vs last wk)" or "  (flat)" or ""
// when both windows are zero. Keeps the email-paste version readable
// without a UI component.
function fmtDelta(current, previous, label = 'vs last wk') {
  if ((current ?? 0) === 0 && (previous ?? 0) === 0) return ''
  const diff = (current ?? 0) - (previous ?? 0)
  if (diff === 0) return `  (flat ${label})`
  const sign = diff > 0 ? '+' : ''
  return `  (${sign}${diff} ${label})`
}
